'use client';

import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { FlashS } from '@lichtblick/photon/icons/energy/flash-s';
import { Checkbox } from '@lichtblick/photon/molecules/checkbox';
import { Stepper } from '@lichtblick/photon/molecules/stepper';
import { Textfield } from '@lichtblick/photon/molecules/textfield';
import { useTracker } from '@lichtblick/tracker';
import { type ChangeEventHandler, type FC, useCallback, useEffect, useState } from 'react';
import type { CalculatorElectricityType } from '../../types/storyblok';
import { CityInput } from '../OAllInOneCalculator/CityInput';
import {
  ELECTRICITY_USAGE,
  gasUsagePerSquareMeter,
  squareMeterLimit,
  usageLimitElectricity,
  usageLimitGas,
} from '../OAllInOneCalculator/constants';
import { UsageModal } from '../OAllInOneCalculator/UsageModal';
import { useVpCodes } from '../OAllInOneCalculator/useVpCodes';
import styles from './calculator.module.scss';

export const CalculatorElectricity: FC<CalculatorElectricityType> = () => {
  const [postcode, setPostcode] = useState('');
  const [city, setCity] = useState('');

  const [people, setPeople] = useState(1);
  const [electricityUsage, setElectricityUsage] = useState(String(ELECTRICITY_USAGE[0]));

  const [addGas, setAddGas] = useState(false);
  const [space, setSpace] = useState(squareMeterLimit.default);
  const [gasUsage, setGasUsage] = useState(String(space * gasUsagePerSquareMeter));

  const [electricityUsageModalOpen, setElectricityUsageModalOpen] = useState(false);
  const [gasUsageModalOpen, setGasUsageModalOpen] = useState(false);

  const { computeVpCodes } = useVpCodes();
  const { trackCustomEvent } = useTracker();

  const cityChangeHandler = useCallback((city: string, postcode: string) => {
    setCity(city);
    setPostcode(postcode);
  }, []);

  const electricityChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setElectricityUsage(e.currentTarget.value);
  }, []);

  const addGasChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setAddGas(e.currentTarget.checked);
  }, []);

  const gasChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setGasUsage(e.currentTarget.value);
  }, []);

  useEffect(() => {
    setElectricityUsage(String(ELECTRICITY_USAGE[Math.min(Math.max(people - 1, 0), 9)]));
  }, [people]);

  useEffect(() => {
    setGasUsage(String(space * gasUsagePerSquareMeter));
  }, [space]);

  const [vpCodes, setVpCodes] = useState<{
    uvp?: string;
    vp?: string;
  }>({});

  useEffect(() => {
    setVpCodes(computeVpCodes());
  }, [computeVpCodes]);

  return (
    <>
      {/** biome-ignore lint/correctness/useUniqueElementIds: There won't be 2 calculators on the same page. needed for jump-mark */}
      <form
        action="/checkout/"
        className={styles.form}
        id="oekostrom-calculator"
        method="GET"
        onSubmit={(e) => {
          if (Number(electricityUsage) > usageLimitElectricity.max) {
            e.preventDefault();
            setElectricityUsageModalOpen(true);

            return;
          }

          if (addGas && Number(gasUsage) > usageLimitGas.max) {
            e.preventDefault();
            setGasUsageModalOpen(true);

            return;
          }

          if (e.currentTarget.checkValidity()) {
            trackCustomEvent({
              eventName: 'tariff_calculator_show_tariffs',
              customProps: {
                product: 'ÖkoStrom',
                zip_code: postcode,
                people_counter: people,
                consumption_calculator: `${electricityUsage} kWh/Jahr`,
                bundle_product: addGas,
              },
            });
          }
        }}
      >
        <Headline className={styles.headline} renderAs="h4" size="s">
          ÖkoStrom
          <FlashS />
        </Headline>
        <CityInput onChange={cityChangeHandler} />
        <Stepper defaultValue={1} label="Personenanzahl" max={10} min={1} onChange={setPeople} />
        <Textfield
          inputMode="numeric"
          label="Stromverbrauch"
          min={usageLimitElectricity.min}
          name="strom"
          onChange={electricityChangeHandler}
          rightSlot={
            <Text className={styles.unit} size="s">
              kWh/Jahr
            </Text>
          }
          type="number"
          value={electricityUsage}
          required
        />
        <Checkbox label={<Checkbox.Label>LichtBlick Gas hinzufügen</Checkbox.Label>}>
          <Checkbox.Checkbox onChange={addGasChangeHandler} />
        </Checkbox>
        {addGas && (
          <>
            <Stepper
              defaultValue={space}
              label="Wohnfläche in m²"
              max={squareMeterLimit.max}
              min={squareMeterLimit.min}
              onChange={setSpace}
              step={squareMeterLimit.step}
            />
            <Textfield
              label="Gasverbrauch"
              min={usageLimitGas.min}
              name="gas"
              onChange={gasChangeHandler}
              rightSlot={<Text size="s">kWh/Jahr</Text>}
              type="number"
              value={gasUsage}
            />
          </>
        )}

        <input name="ort" type="hidden" value={city} />
        <input name="plz" type="hidden" value={postcode} />
        {Boolean(vpCodes.vp) && <input name="vp" type="hidden" value={vpCodes.vp} />}
        {Boolean(vpCodes.uvp) && <input name="uvp" type="hidden" value={vpCodes.uvp} />}

        <BoxCTA as="button" type="submit">
          Tarife berechnen
        </BoxCTA>
      </form>
      <UsageModal isOpen={electricityUsageModalOpen} route="/gewerbestrom" setIsOpen={setElectricityUsageModalOpen} />
      <UsageModal isOpen={gasUsageModalOpen} route="/gewerbegas" setIsOpen={setGasUsageModalOpen} />
    </>
  );
};
